import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  reportTemplateService,
  type CloneReportTemplateDto,
  type CreateReportTemplateDto,
  type UpdateReportTemplateDesignDto,
  type UpdateReportTemplateDto,
} from '@/services/report-template.service';
import { reportTemplateKeys } from './useReportTemplates';

/**
 * Report Designer mutations (B6.9e).
 *
 * Every one of these is a thin wrapper over an EXISTING backend endpoint — no
 * lifecycle is reimplemented here, and no state transition is inferred in the
 * browser. The server's response is what the caller writes back into the cache.
 *
 * Invalidation is deliberately broad on lifecycle changes: publishing or
 * archiving a version changes the family's history AND the library row AND the
 * detail, and a stale "Create New Version" button offering an operation the
 * server would now reject is exactly the failure mode §4 forbids.
 */

export function useCreateReportTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateReportTemplateDto) => reportTemplateService.create(dto),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: reportTemplateKeys.lists() });
    },
  });
}

export function useUpdateReportTemplate(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: UpdateReportTemplateDto) => reportTemplateService.update(id, dto),
    onSuccess: (detail) => {
      qc.setQueryData(reportTemplateKeys.detail(id), detail);
      void qc.invalidateQueries({ queryKey: reportTemplateKeys.lists() });
    },
  });
}

/**
 * B6.9c — the design save.
 *
 * The server's returned document becomes the cached truth immediately
 * (`setQueryData`), not the payload that was sent: the backend normalises
 * positions and may canonicalise widths, and the designer must re-seed from what
 * was actually stored.
 */
export function useUpdateReportTemplateDesign(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: UpdateReportTemplateDesignDto) => reportTemplateService.updateDesign(id, dto),
    onSuccess: (detail) => {
      qc.setQueryData(reportTemplateKeys.detail(id), detail);
      void qc.invalidateQueries({ queryKey: reportTemplateKeys.lists() });
    },
  });
}

export function useCloneReportTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto?: CloneReportTemplateDto }) =>
      reportTemplateService.clone(id, dto ?? {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: reportTemplateKeys.lists() });
    },
  });
}

/** B6.9d — PUBLISHED → a new DRAFT version of the same family. */
export function useCreateReportTemplateRevision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sourceId: string) => reportTemplateService.createRevision(sourceId),
    onSuccess: (_detail, sourceId) => {
      void qc.invalidateQueries({ queryKey: reportTemplateKeys.lists() });
      void qc.invalidateQueries({ queryKey: reportTemplateKeys.versions(sourceId) });
    },
  });
}

export function usePublishReportTemplate(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => reportTemplateService.publish(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: reportTemplateKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: reportTemplateKeys.lists() });
      void qc.invalidateQueries({ queryKey: reportTemplateKeys.versions(id) });
    },
  });
}

export function useArchiveReportTemplate(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => reportTemplateService.archive(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: reportTemplateKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: reportTemplateKeys.lists() });
      void qc.invalidateQueries({ queryKey: reportTemplateKeys.versions(id) });
    },
  });
}

export function useSetDefaultReportTemplate(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => reportTemplateService.setDefault(id),
    onSuccess: () => {
      // The previous default also changed, and it may be any row in the list —
      // so the whole list is invalidated rather than two ids patched.
      void qc.invalidateQueries({ queryKey: reportTemplateKeys.all() });
    },
  });
}

export function useDeleteReportTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => reportTemplateService.remove(id),
    onSuccess: (_v, id) => {
      void qc.invalidateQueries({ queryKey: reportTemplateKeys.lists() });
      void qc.invalidateQueries({ queryKey: reportTemplateKeys.versions(id) });
    },
  });
}
